import { createContext, useEffect, useRef, useState } from "react";

import { useFavouritesContext } from "features/favourites/hooks/useFavouritesContext";
import TagManager from "react-gtm-module";
import whoAmI, { WhoAmIResponse } from "services/motorpoint/whoAmI";

export type WhoAmIProviderContextProps = {
    whoAmIResponse: WhoAmIResponse | undefined;
    isLoggedIn: boolean;
    customerId: string | null | undefined;
    trackingId: string | null | undefined;
} | null;

export type WhoAmIProviderProps = {
    children: React.ReactNode;
};

export const WhoAmIProviderContext = createContext<WhoAmIProviderContextProps>(null);

const WhoAmIProvider = (props: WhoAmIProviderProps) => {
    const {
        userContext: { initUserContextAnonymous, initUserContextForCustomer },
    } = useFavouritesContext();
    const [whoAmIResponse, setWhoAmIResponse] = useState<WhoAmIResponse | undefined>(undefined);
    const hasAttempted = useRef(false);

    useEffect(() => {
        if (hasAttempted.current) return;
        const init = async () => {
            hasAttempted.current = true;
            const whoAmIData = await whoAmI();
            setWhoAmIResponse(whoAmIData);

            if (whoAmIData?.CustomerId) {
                await initUserContextForCustomer({
                    customerId: whoAmIData.CustomerId,
                });
            } else {
                await initUserContextAnonymous();
            }

            TagManager.initialize({
                gtmId: process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
                dataLayer: {
                    event: "globalDataLayer",
                    ...(whoAmIData?.TrackingId && {
                        userId: whoAmIData.TrackingId,
                    }),
                    ...(whoAmIData?.CustomerId && {
                        customerId: whoAmIData.CustomerId,
                    }),
                },
            });
        };

        init().catch(console.error);
    }, [whoAmIResponse, initUserContextAnonymous, initUserContextForCustomer]);

    return (
        <WhoAmIProviderContext.Provider
            value={{
                whoAmIResponse,
                isLoggedIn: !!whoAmIResponse?.CustomerId,
                customerId: whoAmIResponse?.CustomerId,
                trackingId: whoAmIResponse?.TrackingId,
            }}
        >
            {props.children}
        </WhoAmIProviderContext.Provider>
    );
};

export default WhoAmIProvider;
